import React, { Component } from 'react';
import { I18n } from 'react-redux-i18n';
import PropTypes from 'prop-types';
import cx from 'classnames';
import mapValues from 'lodash/mapValues';
import isEqual from 'lodash/isEqual';

import MainOptions from './components/MainOptions';

import '../../styles/Licensing.scss';

export default class Licensing extends Component {
    static propTypes = {
        onChange: PropTypes.func.isRequired,
        useClassBased: PropTypes.bool.isRequired,
        name: PropTypes.string.isRequired,
        options: PropTypes.object.isRequired,
        isAdmin: PropTypes.bool,
        className: PropTypes.string,
        userClasses: PropTypes.array,
        securityClasses: PropTypes.array,
    };

    static defaultProps = {
        userClasses: [],
        securityClasses: [],
    };

    // Static helper to calculate aggregated permissions (can be used in constructor)
    static aggregateClassPermissionsFromProps(props) {
        const { userClasses, securityClasses, options } = props;

        // If no classes or no security classes, return current options
        if (!userClasses || userClasses.length === 0 || !securityClasses || securityClasses.length === 0) {
            return options;
        }

        // Get all licensing options keys from the current options
        const optionKeys = Object.keys(options);

        // Initialize aggregated options with all false
        const aggregatedOptions = optionKeys.reduce((acc, key) => {
            acc[key] = false;
            return acc;
        }, {});

        // For each class the user belongs to, aggregate permissions (OR logic)
        userClasses.forEach(classId => {
            const securityClass = securityClasses.find(sc => sc.id === classId);
            if (securityClass && securityClass.licensing) {
                optionKeys.forEach(key => {
                    // If any class has this permission, set it to true
                    if (securityClass.licensing[key]) {
                        aggregatedOptions[key] = true;
                    }
                });
            }
        });

        return aggregatedOptions;
    }

    state = {
        options: [],
        useClassBased: false,
    };

    constructor(props) {
        super(props);

        // If useClassBased is true on mount, calculate aggregated permissions
        const initialOptions = props.useClassBased
            ? Licensing.aggregateClassPermissionsFromProps(props)
            : props.options;

        this.state = {
            options: initialOptions,
            useClassBased: props.useClassBased,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.name !== this.props.name) {
            const { useClassBased, options } = this.props;

            this.setState({
                useClassBased,
                options,
            });
        }

        // When useClassBased changes to true, aggregate permissions from classes
        if (!prevProps.useClassBased && this.props.useClassBased) {
            const aggregatedOptions = this.aggregateClassPermissions();
            this.setState({
                options: aggregatedOptions,
            }, this.onChange);
        }

        // When userClasses or securityClasses change and useClassBased is true, recalculate permissions
        const userClassesChanged = !isEqual(prevProps.userClasses, this.props.userClasses);
        const securityClassesChanged = !isEqual(prevProps.securityClasses, this.props.securityClasses);

        if ((userClassesChanged || securityClassesChanged) && this.props.useClassBased) {
            const aggregatedOptions = this.aggregateClassPermissions();
            this.setState({
                options: aggregatedOptions,
            }, this.onChange);
        }
    }

    aggregateClassPermissions = (props = this.props) => {
        return Licensing.aggregateClassPermissionsFromProps(props);
    }

    onMainOptionsChange = (options) => {
        if (this.state.useClassBased || this.props.isAdmin) {
            return;
        }

        this.setState(
            (state) => ({
                ...state,
                options,
            }),
            this.onChange
        );
    }

    onClassBasedOptionChange = () => {
        if (this.props.isAdmin) {
            return;
        }

        const newUseClassBased = !this.state.useClassBased;

        this.setState(
            (state) => {
                let newOptions = state.options;

                // If enabling class-based, aggregate permissions from classes
                if (newUseClassBased) {
                    newOptions = this.aggregateClassPermissions();
                }

                return {
                    ...state,
                    useClassBased: newUseClassBased,
                    options: newOptions,
                };
            },
            this.onChange
        );
    }

    onChange = () => {
        const { onChange, name } = this.props;
        const { useClassBased, options } = this.state;

        onChange(name, useClassBased, options);
    }

    render() {
        const { useClassBased, options: stateOptions } = this.state;
        const { options: propsOptions, isAdmin, className } = this.props;

        // Use state options when useClassBased is true, otherwise use props options
        const optionsToUse = useClassBased ? stateOptions : propsOptions;

        return (
            <div className={cx("User-details-settings-licensing", className)}>
                <div
                    className={cx(
                        'User-details-settings-licensing__class-based-option',
                        { 'User-details-settings-licensing__class-based-option-disabled': isAdmin }
                    )}
                >
                    <input
                        type="checkbox"
                        id="User-details-settings-licensing__class-based"
                        checked={useClassBased}
                        onChange={this.onClassBasedOptionChange}
                    />
                    <label htmlFor="User-details-settings-licensing__class-based">{I18n.t('Use class based options')}</label>
                </div>
                <MainOptions
                    className={cx(
                        'User-details-settings-licensing__main-options',
                        { 'User-details-settings-licensing__main-options-disabled': useClassBased || isAdmin }
                    )}
                    options={isAdmin ? mapValues(optionsToUse, () => true) : optionsToUse}
                    onChange={this.onMainOptionsChange}
                />
            </div>
        );
    }
}